import { useEffect } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, useImpersonalization } from '@hooks';
import { ThemeProvider } from '@mui/system';
import { history } from '@helpers/history';
import { alertActions, authActions } from '@actions';
import { authService } from '@services';
import { RoutePath } from '@constants/routes';
import logger from '@widgets/logger';
import theme from '@theme';
// INFO: Features
import AuthRoute from '@features/AuthRoute';
import PrivateRoute from '@features/PrivateRoute';
import NotFoundPage from '@features/NotFoundPage';
import BumpVersion from '@features/BumpVersion';
import SEO from '@features/SEO';
// INFO: Pages
import ErrorPage from '@pages/Error';
import ErrorBoundary from '@features/ErrorBoundary';
import { Toaster } from '@components/Toaster';
import LoginPage from '@pages/Login';
import SignupPage from '@pages/Signup';
import ResetPassword from '@pages/ResetPassword';
import ForgotPassword from '@pages/ForgotPassword';
import AppLayout from '@features/AppLayout';
import AccountSettingsPage from '@pages/AccountSettingsPage';
import FeedbacksPage from '@pages/FeedbacksPage';
import ReviewsPage from '@pages/ReviewsPage';
import OnboardingPage from '@pages/OnboardingPage';
import VerifyEmail from '@pages/VerifyEmail';
import WidgetsPage from '@pages/WidgetsPage';
import ReplyAutomation from '@pages/ReplyAutomation';
// import FeedbackOverview from '@pages/FeedbackOverview';
import SurveysPage from '@pages/SurveysPage';
import ContactsPage from '@pages/ContactsPage';
import CampaignsPage from '@pages/CampaignsPage';
import HomePage from '@pages/HomePage';
import InboxPage from '@pages/InboxPage';
import MicrositesPage from '@pages/MicrositesPage';
import PostsPage from '@pages/PostsPage';
// import InboxTemplates from '@pages/InboxTemplates';
import ProfilesPage from '@pages/ProfilesPage';
import ProfilesMediaPage from '@pages/ProfilesMedia';
import PulseAI from '@pages/PulseAI';
import CreateAccountViaStripe from '@pages/CreateAccountViaStripe';
import CouponsPage from '@pages/CouponsPage';
import PresenceAnalyticsPage from '@pages/Analytics/Presence';
import SocialPostsAnalyticsPage from '@pages/Analytics/SocialPosts';
import ConversationsAnalyticsPage from '@pages/Analytics/Conversations';
import AnalyticsReviewsRoute from '@pages/Analytics/AnalyticsReviewsRoute';
import AnalyticsSurveysRoute from '@pages/Analytics/AnalyticsSurveysRoute';
// INFO: Temporary pages
//import OrdersPage from '@pages/OrdersPage';

const App = () => {
    const user = useSelector(state => state.auth.user);

    const dispatch = useDispatch();
    const { i18n } = useTranslation();

    useImpersonalization();

    useEffect(() => {
        const unlisten = history.listen(() => {
            dispatch(alertActions.clearAll());
        });
        const user = JSON.parse(localStorage.getItem('user'));
        if (user?.token) {
            dispatch(authActions.getCurrentUser());
            sendLastVisit();
        } else {
            dispatch(authActions.setUserLoading(false));
        }
        return unlisten;
    }, [dispatch]);

    useEffect(() => {
        if (user?.language && user?.language !== i18n.language) {
            i18n.changeLanguage(user?.language);
        }
    }, [user?.language, i18n]);

    const sendLastVisit = async () => {
        try {
            await authService.updateLastVisit(new Date());
        } catch (error) {
            logger.error("Couldn't update last visit");
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Router history={history}>
                <ErrorBoundary>
                    <SEO />
                    <BumpVersion />
                    <Switch>
                        <Route
                            exact
                            path={[
                                RoutePath.Home,
                                RoutePath.Microsites,
                                RoutePath.Contacts,
                                RoutePath.Coupons,
                                `${RoutePath.Social}/:page`,
                                `${RoutePath.AccountSettings}/:page`,
                                `${RoutePath.Reviews}/:page?/:path`,
                                `${RoutePath.Surveys}/:page?/:path`,
                                `${RoutePath.Messaging}/:page?/:path`,
                                `${RoutePath.Campaigns}/:page`,
                                `${RoutePath.Presence}/:page`,
                                //'/orders',
                            ]}
                        >
                            <AppLayout>
                                <Switch>
                                    {/* <PrivateRoute exact path="/orders" component={OrdersPage} /> */}
                                    {/* <PrivateRoute exact path="/surveys/sent" component={FeedbackOverview} /> */}
                                    {/* <PrivateRoute exact path="/messaging/templates" component={InboxTemplates} /> */}
                                    <PrivateRoute exact path={RoutePath.Home} component={HomePage} />
                                    <PrivateRoute exact path={RoutePath.SurveysResponses} component={FeedbacksPage} />
                                    <PrivateRoute exact path={RoutePath.SurveysTemplates} component={SurveysPage} />
                                    <PrivateRoute
                                        exact
                                        path={`${RoutePath.SurveysAnalytics}/:path`}
                                        component={AnalyticsSurveysRoute}
                                    />
                                    <PrivateRoute exact path={RoutePath.Microsites} component={MicrositesPage} />
                                    <PrivateRoute exact path={RoutePath.PresenceProfiles} component={ProfilesPage} />
                                    <PrivateRoute exact path={RoutePath.PresenceMedia} component={ProfilesMediaPage} />
                                    <PrivateRoute
                                        exact
                                        path={RoutePath.PresenceAnalytics}
                                        component={PresenceAnalyticsPage}
                                    />
                                    <PrivateRoute exact path={RoutePath.ReviewsManage} component={ReviewsPage} />
                                    <PrivateRoute
                                        exact
                                        path={`${RoutePath.ReviewsAutomation}/:path`}
                                        component={ReplyAutomation}
                                    />
                                    <PrivateRoute exact path={RoutePath.ReviewsWidgets} component={WidgetsPage} />
                                    <PrivateRoute
                                        exact
                                        path={`${RoutePath.ReviewsAnalytics}/:path`}
                                        component={AnalyticsReviewsRoute}
                                    />
                                    <PrivateRoute
                                        exact
                                        path={`${RoutePath.AccountSettings}/:page`}
                                        component={AccountSettingsPage}
                                    />
                                    <PrivateRoute exact path={RoutePath.MessagingInbox} component={InboxPage} />
                                    <PrivateRoute
                                        exact
                                        path={`${RoutePath.MessagingPulseAI}/:path`}
                                        component={PulseAI}
                                    />
                                    <PrivateRoute
                                        exact
                                        path={RoutePath.MessagingAnalytics}
                                        component={ConversationsAnalyticsPage}
                                    />
                                    <PrivateRoute exact path={RoutePath.SocialPosts} component={PostsPage} />
                                    <PrivateRoute
                                        exact
                                        path={RoutePath.SocialAnalytics}
                                        component={SocialPostsAnalyticsPage}
                                    />
                                    <PrivateRoute exact path={RoutePath.CampaignsOneTime} component={CampaignsPage} />
                                    <PrivateRoute exact path={RoutePath.CampaignsAutomated} component={CampaignsPage} />
                                    <PrivateRoute exact path={RoutePath.Contacts} component={ContactsPage} />
                                    <PrivateRoute exact path={RoutePath.Coupons} component={CouponsPage} />
                                    {/* Redirects from base URL */}
                                    <Redirect from={RoutePath.Surveys} to={RoutePath.SurveysResponses} />
                                    <Redirect from={RoutePath.Reviews} to={RoutePath.ReviewsManage} />
                                    <Redirect from={RoutePath.Messaging} to={RoutePath.MessagingInbox} />
                                    <Redirect from={RoutePath.Social} to={RoutePath.SocialPosts} />
                                    <Redirect from={RoutePath.AccountSettings} to={RoutePath.AccountSettingsProfile} />
                                    <Redirect from={RoutePath.Presence} to={RoutePath.PresenceProfiles} />
                                </Switch>
                            </AppLayout>
                        </Route>
                        <PrivateRoute exact path={RoutePath.Onboarding} component={OnboardingPage} />
                        <AuthRoute exact path={RoutePath.Login} component={LoginPage} />
                        <AuthRoute exact path={RoutePath.Signup} component={SignupPage} />
                        <Route path={`${RoutePath.ResetPassword}/:token`} component={ResetPassword} />
                        <Route exact path={RoutePath.ForgotPassword} component={ForgotPassword} />
                        <Route exact path={`${RoutePath.VerifyEmail}/:token`} component={VerifyEmail} />
                        <Route exact path={RoutePath.StripeAccountCreation} component={CreateAccountViaStripe} />
                        <Route exact path={RoutePath.Error} component={ErrorPage} />
                        <Route path={RoutePath.NotFound} exact component={NotFoundPage} />
                    </Switch>
                    <Toaster />
                </ErrorBoundary>
            </Router>
        </ThemeProvider>
    );
};

export default App;
